import { useRecoilCallback } from "recoil";
import { SheetProduct } from "@/features/order-sheets/sheet.type";
import {
  FlatSheetKey,
  InflatedProduct,
  inflatedTabsFamily,
} from "@/features/line-sheet-sets/helpers/sheet-state";
import { produce } from "immer";
import { ProductPrice } from "@/features/types";
import {
  CommissionType,
  PriceAdjustedBy,
} from "@/features/line-sheet-sets/line-sheet-set.types";

function calcSupply(product: SheetProduct, priceConfig?: PriceConfig): {
  confirmedPrice: ProductPrice | null;
  supplyPrice: ProductPrice | null;
} {
  let confirmedPrice = null;
  let supplyPrice = null;
  if (product.confirmedPriceAdjustedBy && product.confirmedPriceWithoutCondition) {
    const by = product.confirmedPriceAdjustedBy?.by
    let confirmedPriceWithoutCondition = null;
    confirmedPriceWithoutCondition = product.confirmedPriceWithoutCondition

    if (confirmedPriceWithoutCondition.amount === 0) {
      if (by === "MARKUP") {
        confirmedPriceWithoutCondition = product.costPrice
      } else {
        confirmedPriceWithoutCondition = product.retailPrice
      }
    }
    confirmedPrice = calcPrice(
      product.confirmedPriceAdjustedBy,
      confirmedPriceWithoutCondition,
      confirmedPriceWithoutCondition,
      product.supplyPrice,
      priceConfig
    );
  }

  if (product.priceAdjustedBy) {
    if (product.isConditionFixed) {
      // 고정가는 base 그대로 유지(반복 계산으로 감소 방지)
      supplyPrice = product.supplyPrice;
    } else {
      supplyPrice = calcPrice(
        product.priceAdjustedBy,
        product.costPrice,
        product.retailPrice,
        product.supplyPrice,
        priceConfig
      );
    }
  }

  return {
    confirmedPrice,
    supplyPrice,
  };
}

// 라인시트세트의 수수료 (최종가 계산용)
export interface PriceConfig {
  commission?: number | null;
  // 수수료 계산 단위. ORDER면 상품 단위 공급가에는 수수료를 적용하지 않고 총 주문금액에만 반영한다.
  commissionType?: CommissionType | null;
}

// '/1.22', '*0.97', '+3000', '-5000' 같은 IVA 계산식을 base에 적용 (빈 값/형식 오류면 base 그대로)
function applyPriceExpr(base: number, expr: string | null | undefined): number {
  if (!expr) return base;
  const e = String(expr).trim().replace(/\s/g, "");
  if (!e) return base;
  const op = e[0];
  const num = parseFloat(e.slice(1));
  if (!["+", "-", "*", "/"].includes(op) || Number.isNaN(num)) return base;
  if (op === "+") return base + num;
  if (op === "-") return base - num;
  if (op === "*") return base * num;
  return num === 0 ? base : base / num; // "/"
}

export function calcPrice(
  condition: PriceAdjustedBy | null,
  costPrice: ProductPrice | null,
  retailPrice: ProductPrice | null,
  previousSupplyPrice: ProductPrice | null,
  priceConfig?: PriceConfig
): ProductPrice | null {
  if (condition) {
    const { by, value, applyIva, ivaValue } = condition;
    let supplyPrice;

    // 수수료(commission)를 곱하고 소수점 둘째 자리 반올림.
    // 화면에 보이는 단가(통화 포맷이 둘째 자리까지 표시)와 실제 계산에 쓰이는 값을 일치시켜서,
    // 단가를 그대로 더한 값이 합계와 어긋나지 않게 한다.
    // 단, commissionType이 ORDER(총 주문금액)인 경우 상품 단위 공급가에는 수수료를 적용하지 않는다.
    const commission =
      priceConfig?.commissionType === "ORDER" ? null : priceConfig?.commission;
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const withCommission = (amount: number) =>
      round2(commission ? amount * (1 + commission / 100) : amount);

    if (!costPrice && !retailPrice && previousSupplyPrice) {
      // cost와 retial이 없는데(0인데) supply price가 주어져 있다면, supply price를 수정하지 않는다.
      supplyPrice = {
        amount: previousSupplyPrice.amount,
        currency: previousSupplyPrice.currency,
      };
    } else if (by === "MARKUP" && costPrice) {
      supplyPrice = {
        amount: withCommission(costPrice.amount * (1 + value / 100)),
        currency: costPrice.currency,
      };
    } else if (
      by === "DISCOUNT" &&
      retailPrice &&
      retailPrice.currency === "KRW"
    ) {
      // 원화인 경우 국내 재고이기 때문에 IVA를 제외하지 않는다.
      supplyPrice = {
        amount: withCommission(retailPrice.amount * (1 - value / 100)),
        currency: retailPrice.currency,
      };
    } else if (by === "DISCOUNT" && retailPrice) {
      let base = retailPrice.amount * (1 - value / 100);
      // value가 0%여도 IVA 계산식은 적용한다. (적용 여부는 applyIva만으로 결정)
      const skipIva = applyIva === false;
      if (!skipIva) {
        // 상품 컨디션별 IVA 계산식(기본 /1.22)으로 계산
        base = applyPriceExpr(base, ivaValue || "/1.22");
      }
      supplyPrice = {
        amount: withCommission(base),
        currency: retailPrice.currency,
      };
    } else {
      supplyPrice = null;
    }
    return supplyPrice;
  }
  return null;
}

// 고정 컨디션(isConditionFixed)이라도 IVA 계산식/수수료가 컨펌 금액에 반영되도록 calcSupply로 재계산한다.
// (calcSupply의 supplyPrice는 고정가를 그대로 보존하므로 부티크 확정 공급가는 바뀌지 않는다)
// 단, 재계산이 불가한 경우(컨펌 컨디션/베이스 없음)엔 부티크가 확정한 고정 공급가를 confirmed로 유지한다.
function resolveSupplyAndConfirmed(product: SheetProduct, priceConfig?: PriceConfig): {
  confirmedPrice: ProductPrice | null;
  supplyPrice: ProductPrice | null;
} {
  const result = calcSupply(product, priceConfig);
  if (product.isConditionFixed && product.supplyPrice) {
    return {
      supplyPrice: result.supplyPrice ?? product.supplyPrice,
      confirmedPrice:
        result.confirmedPrice ?? {
          amount: product.supplyPrice.amount,
          currency: product.supplyPrice.currency,
        },
    };
  }
  return result;
}

export default function useInflatedTabs() {
  const updateInflatedProduct = useRecoilCallback(
    ({ set }) =>
      (key: FlatSheetKey, tabIndex: number, nextProduct: InflatedProduct, priceConfig?: PriceConfig) => {
        set(inflatedTabsFamily(key), (prevState) => {
          if (prevState && tabIndex > -1) {
            return produce(prevState, (draft) => {
              const draftTabIndex = draft.tabs.findIndex(
                (tab) => tab.index === tabIndex
              );

              if (draftTabIndex > -1) {
                const productIndex = draft.tabs[
                  draftTabIndex
                ].products.findIndex(
                  (product) => product.id === nextProduct.id
                );

                if (productIndex > -1) {
                  draft.tabs[draftTabIndex].products[productIndex] = {
                    ...nextProduct,
                    ...resolveSupplyAndConfirmed(nextProduct, priceConfig),
                    isDirty: true
                  };
                }
                draft.tabs[draftTabIndex].isDirty = true;
              }
            });
          }

          return prevState;
        });
      },
    []
  );

  const updateInflatedProducts = useRecoilCallback(
    ({ set }) =>
      (
        key: FlatSheetKey,
        tabIndex: number,
        nextProducts: InflatedProduct[],
        priceConfig?: PriceConfig
      ) => {
        set(inflatedTabsFamily(key), (prevState) => {
          if (prevState && tabIndex > -1) {
            return produce(prevState, (draft) => {
              const draftTabIndex = draft.tabs.findIndex(
                (tab) => tab.index === tabIndex
              );

              if (draftTabIndex > -1) {
                nextProducts.forEach((nextProduct) => {
                  const productIndex = draft.tabs[
                    draftTabIndex
                  ].products.findIndex(
                    (product) => product.id === nextProduct.id
                  );
                  if (productIndex > -1) {
                    draft.tabs[draftTabIndex].products[productIndex] = {
                      ...nextProduct,
                      ...resolveSupplyAndConfirmed(nextProduct, priceConfig),
                      isDirty: true,
                    };
                  }
                });
                draft.tabs[draftTabIndex].isDirty = true;
              }
            });
          }
          return prevState;
        });
      },
    []
  );

  const getInflatedProductById = useRecoilCallback(
    ({ snapshot }) =>
      (key: FlatSheetKey, tabIndex: number, id: number) => {
        const inflatedTabsLodable = snapshot.getLoadable(
          inflatedTabsFamily(key)
        );
        if (inflatedTabsLodable.state === "hasValue") {
          const inflatedTabsState = inflatedTabsLodable.contents;
          if (inflatedTabsState) {
            const tab = inflatedTabsState.tabs.find(
              (t) => t.index === tabIndex
            );
            if (tab) {
              return tab.products.find((p) => p.id === id);
            }
          }
        } else {
          throw {
            code: -1,
            status: "SORRY_MY_BAD",
            message: "I thought synchronous atomFamily always has value",
          };
        }
      },
    []
  );

  //여기서도 filter하고 useFetchAndSetLineSheetTab에서도 필터하는구낭..
  //filter작업이 두곳에서 일어나는구만... 합치는게 좋은데..
  const filterInflatedProducts = useRecoilCallback(
    ({ set }) =>
      (
        key: FlatSheetKey,
        tabIndex: number,
        filter: (product: SheetProduct) => boolean,
        orderBy: string,
        isDirty: boolean
      ) => {
        set(inflatedTabsFamily(key), (prevState) => {
          if (prevState) {
            return produce(prevState, (draft) => {
              if (tabIndex !== -1) {
                const draftTabIndex = draft.tabs.findIndex(
                  (tab) => tab.index === tabIndex
                );

                if (draft.tabs[draftTabIndex]) {
                  draft.tabs[draftTabIndex].products = draft.tabs[
                    draftTabIndex
                  ].products.sort(
                    (a, b) => {
                      if (a.supplyPrice !== null && b.supplyPrice !== null) {
                        if (orderBy === "price_asc") {
                          return a.supplyPrice.amount - b.supplyPrice.amount;
                        } else if (orderBy === "price_desc") {
                          return b.supplyPrice.amount - a.supplyPrice.amount;
                        } else {
                          return a.id - b.id;
                        }
                      } else {
                        return 0;
                      }
                    }
                  ).map((product) => {
                    return {
                      ...product,
                      isFiltered: filter(product),
                    };
                  });
                  // prevState에서 isDirty가 false로 초기화되서 들어옴.
                  // useFetchAndSetOrderSheetTabs 가 호출되어서 여기서 초기화된 이후 이곳에 진입하게 되어서 그런걸로 추정됨.
                  // 우선 저장/주문 버튼이 켜져있을 때 필터를 변경하면 저장버튼이 비활성화되는 문제를 해결하기 위해
                  // isDirty를 order-sheet-set-page.tsx에서 받아와서 업데이트 쳐주는 걸로 해결함.
                  // https://uno-trading.slack.com/archives/C064JSFQ0TV/p1735797374776269
                  draft.tabs[draftTabIndex].isDirty = isDirty;
                }
              }
            });
          }
          return prevState;
        });
      },
    []
  );

  const resetInflatedSheet = useRecoilCallback(
    ({ reset }) =>
      (key: FlatSheetKey) => {
        reset(inflatedTabsFamily(key));
      },
    []
  );

  return {
    updateInflatedProduct,
    updateInflatedProducts,
    getInflatedProductById,
    filterInflatedProducts,
    resetInflatedSheet,
  };
}
